<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const isOpen = ref(false);
const closeMenu = () => {
  isOpen.value = false;
};

const selectedOption = computed(() =>
  props.options.find(option => option.value === props.modelValue)
);
const hasSelection = computed(
  () => selectedOption.value && selectedOption.value.value !== ''
);

const selectedLabel = computed(
  () => selectedOption.value?.label || props.placeholder
);

const selectOption = value => {
  emit('update:modelValue', value);
  isOpen.value = false;
};
</script>

<template>
  <div v-on-click-outside="closeMenu" class="contact-dropdown">
    <button
      type="button"
      class="contact-dropdown__trigger"
      :class="{ 'is-open': isOpen, 'is-placeholder': !hasSelection }"
      :disabled="disabled"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span class="truncate">{{ selectedLabel }}</span>
      <Icon
        icon="i-lucide-chevron-down"
        class="contact-dropdown__chevron"
      />
    </button>
    <div v-if="isOpen" class="contact-dropdown__menu" role="menu">
      <button
        v-for="option in options"
        :key="String(option.value)"
        type="button"
        role="menuitemradio"
        class="contact-dropdown__option"
        :class="{ 'is-selected': option.value === modelValue }"
        :aria-checked="option.value === modelValue"
        @click="selectOption(option.value)"
      >
        <span class="contact-dropdown__option-label">{{ option.label }}</span>
        <Icon
          v-if="option.value === modelValue"
          icon="i-lucide-check"
          class="contact-dropdown__check"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
.contact-dropdown {
  position: relative;
  width: 100%;
}

.contact-dropdown__trigger {
  display: flex;
  width: 100%;
  height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 0.75rem;
  border: 1px solid var(--border-color, rgb(var(--border-weak)));
  border-radius: 10px;
  outline: none;
  background: var(--bg-tertiary, #fff);
  color: var(--text-primary, rgb(var(--slate-12)));
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.contact-dropdown__trigger:hover {
  border-color: rgb(var(--slate-7));
}

.contact-dropdown__trigger:focus-visible,
.contact-dropdown__trigger.is-open {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}

.contact-dropdown__trigger.is-placeholder {
  color: rgb(var(--slate-9));
}

.contact-dropdown__trigger:disabled {
  border-color: rgb(var(--border-weak));
  background: rgb(var(--slate-2));
  color: rgb(var(--slate-10));
  cursor: not-allowed;
}

.contact-dropdown__chevron {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
  color: rgb(var(--slate-10));
  transition: transform 0.15s ease;
}

.contact-dropdown__trigger.is-open .contact-dropdown__chevron {
  transform: rotate(180deg);
}

.contact-dropdown__menu {
  position: absolute;
  z-index: 60;
  top: calc(100% + 0.35rem);
  left: 0;
  display: grid;
  width: 100%;
  max-height: 16rem;
  gap: 2px;
  overflow-y: auto;
  padding: 0.3rem;
  border: 1px solid var(--border-color, rgb(var(--border-weak)));
  border-radius: 10px;
  background: var(--bg-tertiary, #fff);
  box-shadow:
    0 10px 24px rgba(15, 23, 42, 0.12),
    0 2px 5px rgba(15, 23, 42, 0.06);
}

.contact-dropdown__option {
  display: flex;
  min-height: 36px;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.65rem;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--text-primary, rgb(var(--slate-12)));
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    color 0.12s ease;
}

.contact-dropdown__option:hover,
.contact-dropdown__option:focus-visible {
  outline: none;
  background: rgb(var(--slate-2));
}

.contact-dropdown__option.is-selected {
  background: rgba(248, 113, 113, 0.1);
  color: var(--text-primary, rgb(var(--slate-12)));
  font-weight: 600;
}

.contact-dropdown__check {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
  color: #ef4444;
}
</style>
